import { createRng } from '../../../core/rng';
import { Grid, hits } from '../song';
import type { Note } from '../song';
import { barsOf, blend, cellsOf, chooser, cycle, find, fitForm, midi, noteName, pc, pcs, phrase, sizeOf, titleOf, totalBars, trackId, transpose } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';

type Chord = { root: string; stab: readonly string[]; hold: readonly string[] };

const CHORDS: Readonly<Record<string, Chord>> = {
  C: { root: 'C2', stab: ['C4', 'E4', 'G4'], hold: ['E4', 'G4', 'C5'] },
  F: { root: 'F2', stab: ['C4', 'F4', 'A4'], hold: ['F4', 'A4', 'C5'] },
  G7: { root: 'G2', stab: ['B3', 'D4', 'F4'], hold: ['D4', 'F4', 'B4'] },
  Am: { root: 'A2', stab: ['C4', 'E4', 'A4'], hold: ['E4', 'A4', 'C5'] },
  Dm: { root: 'D2', stab: ['D4', 'F4', 'A4'], hold: ['D4', 'F4', 'A4'] },
  Bb: { root: 'Bb2', stab: ['D4', 'F4', 'Bb4'], hold: ['D4', 'F4', 'Bb4'] },
};

const PROGS = [
  ['C', 'F', 'G7', 'C'],
  ['C', 'Am', 'F', 'G7'],
  ['C', 'G7', 'G7', 'C'],
  ['C', 'Bb', 'F', 'C'],
  ['C', 'Dm', 'G7', 'C'],
] as const;

const HOOK: Note[][] = [
  [[0, 'G5', 3], [3, 'E5', 3], [6, 'G5', 2], [8, 'C6', 3], [11, 'B5', 1, 0.7], [12, 'A5', 2], [14, 'G5', 2]],
  [[0, 'A5', 3], [3, 'F5', 3], [6, 'A5', 2], [8, 'C6', 4], [12, 'A5', 2], [14, 'F5', 2]],
  [[0, 'G5', 2], [2, 'F5', 1, 0.7], [3, 'G5', 3], [6, 'B5', 2], [8, 'D6', 3], [11, 'C6', 1, 0.7], [12, 'B5', 2], [14, 'G5', 2]],
  [[0, 'E5', 3], [3, 'G5', 3], [6, 'C6', 6], [12, 'D5', 1, 0.7], [13, 'E5', 1, 0.8], [14, 'F5', 2]],
];

const BASSES: readonly (readonly [step: number, semis: number, len: number][])[] = [
  [[0, 0, 2.6], [3, 0, 0.9], [4, 7, 3.4], [8, 0, 2.6], [11, 0, 0.9], [12, 7, 3.4]],
  [[0, 0, 2.6], [3, 0, 0.9], [4, 7, 3.4], [8, 0, 2.6], [11, 7, 0.9], [12, 12, 1.8], [14, 7, 1.6]],
  [[0, 0, 2.6], [3, 12, 0.9], [4, 7, 3.4], [8, 0, 2.6], [11, 12, 0.9], [12, 7, 3.4]],
];

const VIOLAS: readonly (readonly [step: number, tone: number, vel: number][])[] = [
  [[0, 0, 0.9], [2, 1, 0.5], [3, 2, 0.6], [4, 1, 0.7], [6, 2, 0.5], [8, 0, 0.85], [10, 1, 0.5], [11, 2, 0.6], [12, 1, 0.7], [14, 2, 0.5]],
  [[0, 0, 0.9], [3, 1, 0.6], [4, 2, 0.7], [6, 1, 0.5], [7, 2, 0.5], [8, 0, 0.85], [11, 1, 0.6], [12, 2, 0.7], [14, 1, 0.5]],
];

const KEYS = [0, 2, -3, -2, -1, -5, 3] as const;
const MAJOR = [0, 2, 4, 5, 7, 9, 11] as const;
const ZABUMBAS = ['X..x....X..x....', 'X..x....X..x..x.', 'X..x...xX..x....'] as const;
const FILLS = ['X..x....X.x.x.xx', 'X..x..x.X.x.x.X.', 'X..x....Xxx.X.xx'] as const;
const BACALHAUS = ['..x...x...x...x.', '..x...x.x.x...x.'] as const;
const TRIANGLES = ['xo.oxo.oxo.oxo.o', 'Xo.oxo.oxo.oXo.o'] as const;
const OPEN_TRIANGLE = '..X...X...X...X.';
const JINGLES = ['.-x-.-x-.-x-.-x-', '.-x-.-xx.-x-.-x-'] as const;
const OFFBEATS = '..x...x...x...x.';
const GANZA = 'x-x-x-x-x-x-x-x-';
const ROLL = 'x.x.x.x.xxxxxxxx';

const PLAN: FormPlan = {
  intro: 4,
  outro: 4,
  body: [
    { name: 'A', bars: 8, min: 4, rank: 2 },
    { name: 'drop', bars: 8, min: 4, rank: 1 },
    { name: 'break', bars: 4, min: 4, rank: 4, with: 'drop2' },
    { name: 'drop2', bars: 8, min: 4, rank: 3 },
  ],
};

function under(note: string, tones: readonly number[]): string {
  const m = midi(note);
  const scale = blend([...MAJOR], tones);
  for (const d of [3, 4]) if (scale.includes(pc(m - d))) return noteName(m - d);
  for (let d = 1; d <= 6; d++) if (tones.includes(pc(m - d))) return noteName(m - d);
  return noteName(m - 3);
}

function score(seed: string, p: Params): Score {
  const ch = chooser(FORRO, seed);
  const key = ch.pick(KEYS);
  const prog = ch.pick(PROGS).map((n) => CHORDS[n]!);
  const zabP = ch.pick(ZABUMBAS);
  const fillP = ch.pick(FILLS);
  const bacP = ch.pick(BACALHAUS);
  const triP = ch.pick(TRIANGLES);
  const jingleP = ch.pick(JINGLES);
  const bassRow = ch.pick(BASSES);
  const violaRow = ch.pick(VIOLAS);
  const hook = ch.orig
    ? HOOK
    : phrase(
        ch.rng,
        HOOK.map(cellsOf),
        prog.map((c) => [{ at: 0, tones: pcs([c.root, ...c.stab]) }]),
        MAJOR,
        [72, 86],
        79,
      );
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);
  const low = (n: string) => {
    let m = midi(tr(n));
    while (m < 33) m += 12;
    while (m > 44) m -= 12;
    return noteName(m);
  };

  const form = fitForm(barsOf(p), PLAN);
  const g = new Grid(p.bpm, 53, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const drop1 = find(form, 'drop')!;
  const drop2 = find(form, 'drop2');
  const brk = find(form, 'break');
  const groove1 = find(form, 'A');
  const rng = createRng(`forro-${seed}`);
  const human = (v: number) => v * (0.92 + rng.float() * 0.16);

  const events: Ev[] = [];
  const hit = (v: string, bar: number, step: number, vel: number) => events.push({ v, t: g.at(bar, step), bar, step, vel });
  const play = (v: string, bar: number, step: number, note: string, dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, note });
  const chord = (v: string, bar: number, step: number, notes: readonly string[], dur: number, vel: number, strum = 0) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, notes, strum });

  const autos: Score['autos'] = [
    { v: 'accLp', points: [[g.at(0), 1700], [g.at(intro), 4600]] },
    { v: 'accLp', points: [[g.at(total - 1), 4600], [g.at(total), 1800]] },
  ];

  for (const b of g.bars()) {
    const i = cycle(b.bar - intro, 4);
    const end = b.bar === total - 1;
    const c = end ? CHORDS.C! : prog[i]!;
    const tones = pcs([c.root, ...c.stab]);
    const drop = b.name === 'drop' || b.name === 'drop2';
    const band = drop || b.name === 'A' || (b.name === 'outro' && !end);
    const stab = c.stab.map(tr);

    if (end) {
      hit('zabumba', b.bar, 0, 1);
      hit('crash', b.bar, 0, 0.9);
      hit('impact', b.bar, 0, 0.8);
      chord('stab', b.bar, 0, stab, s * 3, 0.95);
      chord('pad', b.bar, 0, c.hold.map(tr), g.barLength * 0.9, 0.85, 0.012);
      play('bass', b.bar, 0, low(c.root), g.barLength * 0.6, 0.9);
      play('sub', b.bar, 0, low(c.root), g.barLength * 0.8, 0.9);
      continue;
    }

    if (band) {
      const pattern = b.index % 4 === 3 ? fillP : zabP;
      for (const h of hits(pattern)) hit('zabumba', b.bar, h.step, human(h.vel));
      for (const h of hits('....x.......x...')) hit('ta', b.bar, h.step, human(0.6));
      for (const h of hits(bacP)) hit('bacalhau', b.bar, h.step, human(h.vel * 0.9));
      for (const h of hits(triP)) hit('triC', b.bar, h.step, human(h.vel));
      for (const h of hits(OPEN_TRIANGLE)) hit('triO', b.bar, h.step, human(0.8));
      if (drop || e >= 0.7) for (const h of hits(jingleP)) hit('jingle', b.bar, h.step, human(h.vel));
      for (const h of hits(GANZA)) hit('ganza', b.bar, h.step, human(h.vel * (drop ? 1 : 0.8)));
      if (drop) for (const h of hits('....X.......X...')) hit('clap', b.bar, h.step, human(1));
      else if (e >= 0.5) for (const h of hits('....X.......X...')) hit('clap', b.bar, h.step, human(0.8));
      const root = midi(low(c.root));
      for (const [step, semis, len] of bassRow) {
        const note = noteName(root + semis);
        play('bass', b.bar, step, note, len * s * 0.95, human(0.9));
        play('sub', b.bar, step, note, len * s * 0.95, 0.9);
      }
      for (const h of hits(OFFBEATS)) chord('stab', b.bar, h.step, stab, s * 1.3, human(0.75));
      if (drop || e >= 0.7) {
        for (const [step, k, vel] of violaRow) play('viola', b.bar, step, stab[k]!, s * 1.8, human(vel));
      }
    } else if (b.name === 'break') {
      for (const h of hits('X.......X.......')) hit('zabumba', b.bar, h.step, human(0.95));
      for (const h of hits(triP)) hit('triC', b.bar, h.step, human(h.vel * 0.8));
      for (const h of hits(OPEN_TRIANGLE)) hit('triO', b.bar, h.step, human(0.6));
      for (const h of hits(bacP)) hit('bacalhau', b.bar, h.step, human(h.vel * 0.5));
      play('sub', b.bar, 0, low(c.root), s * 14, 0.7);
      chord('pad', b.bar, 0, c.hold.map(tr), g.barLength * 0.96, 0.8, 0.01);
    } else if (b.name === 'intro') {
      chord('pad', b.bar, 0, c.hold.map(tr), g.barLength * 0.96, 0.7, 0.012);
      if (b.index >= b.bars / 2) for (const h of hits(triP)) hit('triC', b.bar, h.step, human(h.vel * 0.7));
      if (b.last) for (const h of hits('............x.x.')) hit('zabumba', b.bar, h.step, 0.78);
    }

    const row = hook[i]!;
    const lead = (vel: number, harm: number, octave: boolean) => {
      for (const [step, note, len, v] of row) {
        const n = tr(note);
        play('lead', b.bar, step, n, len * s * 0.92, human((v ?? 0.95) * vel));
        if (harm > 0) play('harm', b.bar, step, tr(under(note, tones)), len * s * 0.9, (v ?? 0.9) * harm);
        if (octave) play('harm', b.bar, step, transpose(n, -12), len * s * 0.88, (v ?? 0.9) * 0.45);
      }
    };
    if (b.name === 'A') lead(0.85, 0, false);
    if (b.name === 'drop') lead(1, 0.6, e >= 0.85);
    if (b.name === 'drop2') lead(1, 0.6, e >= 0.6);
    if (b.name === 'break' && b.index < b.bars / 2) lead(0.8, 0, false);

    if (b.name === 'A' && b.last) {
      for (const h of hits('........x.x.xxxx')) hit('snare', b.bar, h.step, 0.35 + (0.6 * h.step) / 16);
    }
    if (b.name === 'break' && b.last) {
      for (const h of hits(ROLL)) hit('snare', b.bar, h.step, 0.3 + (0.65 * h.step) / 16);
    }
  }

  events.push({ v: 'rise', t: g.at(Math.max(intro, drop1 - 2)), end: g.at(drop1) - s * 2, bar: Math.max(intro, drop1 - 2), step: 0, vel: 1 });
  if (drop2 !== undefined && brk !== undefined) {
    const from = brk + sizeOf(form, 'break') / 2;
    events.push({ v: 'rise', t: g.at(from), end: g.at(drop2) - s * 2, bar: from, step: 0, vel: 1 });
    events.push({ v: 'fall', t: g.at(brk), end: g.at(brk + 1), bar: brk, step: 0, vel: 1 });
  }
  for (const bar of [drop1, drop2]) {
    if (bar === undefined) continue;
    hit('crash', bar, 0, 1);
    hit('impact', bar, 0, 1);
  }
  if (groove1 !== undefined) hit('crash', groove1, 0, 0.7);

  return {
    id: trackId(FORRO, seed, p),
    template: FORRO.id,
    seed,
    params: p,
    title: ch.orig ? FORRO.ref.title : titleOf(seed),
    genre: FORRO.genre,
    bpm: p.bpm,
    swing: 53,
    lufs: -10.5,
    key,
    form,
    mix: { reverbDecay: 1.9, delayTime: (60 / p.bpm) * 0.75, delayFeedback: 0.24, drive: 1.5, softness: 1.3, pumpDepth: 0.4, pumpRelease: 0.22 },
    events,
    autos,
    accents: { zabumba: 'kick', clap: 'snare', stab: 'stab', lead: 'melody', impact: 'drop' },
  };
}

export const FORRO: Template = {
  id: 'forro',
  genre: 'forró / baião',
  bpm: [116, 132],
  ref: { seed: 'circulo', title: 'Círculo', params: { bpm: 120, bars: 24, energy: 0.55 } },
  score,
};
